import React from "react";
import { Link } from "react-router-dom";

/**
 * Phase 7a — Terms placeholder.
 *
 * Short-form placeholder while legal review is pending. Reproduces the
 * mandated disclaimer alongside minimal usage terms. Full commercial
 * agreement text will replace this page before Phase 7 goes to a first
 * paying customer.
 */
export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="terms-page">
      <header className="border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-30">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-slate-900 text-white text-xs font-bold tracking-widest">
              RA
            </span>
            <span className="text-sm font-semibold">RecoveryAssure</span>
          </Link>
          <Link to="/login" className="text-sm text-slate-700 hover:text-slate-900 underline">
            Sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-12 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Terms of use
          </div>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight">
            Terms — summary
          </h1>
          <p className="mt-3 text-xs text-slate-500 italic">
            Legal review pending — a full agreement will replace this page
            before any commercial engagement.
          </p>
        </div>

        <section
          className="text-sm text-slate-700 leading-relaxed space-y-3"
          aria-labelledby="terms-summary"
        >
          <h2 id="terms-summary" className="sr-only">
            Summary
          </h2>
          <p>
            By using RecoveryAssure you agree to use the service for its
            intended purpose — analysing your own business continuity data —
            and to not attempt to bypass tenant isolation, access other
            customers' workspaces, or interfere with the deterministic engine
            or audit-receipt tamper evidence.
          </p>
          <p>
            <strong>Accounts.</strong> Access is workspace-scoped; each
            workspace has an explicit membership list managed by workspace
            admins.
          </p>
          <p>
            <strong>Data ownership.</strong> You retain full ownership of the
            data you upload. RecoveryAssure operates the analytical service
            over that data but does not claim rights to it.
          </p>
          <p>
            <strong>Availability.</strong> RecoveryAssure is provided on an
            as-is basis. Beta and trial workspaces make no service-level
            guarantees; commercial tiers will document SLAs in the full
            agreement.
          </p>
        </section>

        <section
          aria-labelledby="terms-disclaimer"
          className="rounded border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 leading-relaxed"
        >
          <h2
            id="terms-disclaimer"
            className="text-sm font-semibold text-slate-900"
          >
            Disclaimer
          </h2>
          <p className="mt-2">
            RecoveryAssure produces analytical findings based on the data you
            provide. Findings are not a substitute for professional business
            continuity or risk-management advice. Your organisation remains
            responsible for its recovery strategy and regulatory obligations.
            RecoveryAssure does not guarantee that any process, system, or
            dependency will meet its recovery objectives.
          </p>
        </section>

        <div className="pt-6 border-t border-slate-200 text-xs text-slate-500">
          Return to{" "}
          <Link to="/" className="underline text-slate-700">home</Link>
          .
        </div>
      </main>
    </div>
  );
}
